import { extractParagraphsFromContentBlocks } from './extractParagraphsFromContentBlocks';
import { Queries } from '../../slate-api';
import type { TableCellElement, TableElement } from '../../slate-api';
import { arrayEntries } from '../../util';
import type { CellRange, Context, ExtractedParagraph } from '../shared/types';

interface TableCellWithRange {
  cellRange: CellRange;
  path: number[];
  tableCell: TableCellElement;
}

export function extractParagraphsFromTable(
  table: TableElement,
  context: Context
): ExtractedParagraph[] {
  const tableCellsWithRanges: TableCellWithRange[] = [];

  /**
   * Map from column index to the most recently encountered table cell in that column. This is used
   * to mutate the cellRange.end.row property for each subsequently encountered table cell in the
   * same column with a verticalMerge property of 'continue'.
   */
  const lastTableCellInColumn = new Map<number, TableCellWithRange>();

  for (const [tableRowIndex, tableRow] of arrayEntries(table.children)) {
    let startColumnIndex = 0;

    for (const [tableCellIndex, tableCell] of arrayEntries(tableRow.children)) {
      const verticalMerge = Queries.renderedTableCellVerticalMerge(tableCell);

      const endColumnIndex = Queries.getTableCellEndColumnFromStartColumn(
        tableCell,
        startColumnIndex
      );

      /**
       * If this cell has a verticalMerge property of 'continue', check if the previous cell in the
       * same column has with a verticalMerge property that isn't undefined. If so, mutate that
       * previous cell's metadata so that it ends on the current row, and do not process the current
       * cell any further.
       */
      if (verticalMerge === 'continue') {
        const lastTableCellInfo = lastTableCellInColumn.get(startColumnIndex);

        if (
          lastTableCellInfo &&
          Queries.renderedTableCellVerticalMerge(lastTableCellInfo.tableCell) !== undefined
        ) {
          lastTableCellInfo.cellRange.end.row = tableRowIndex;
          startColumnIndex = endColumnIndex + 1;
          continue;
        }
      }

      const start = { column: startColumnIndex, row: tableRowIndex };
      const end = { column: endColumnIndex, row: tableRowIndex };
      const cellRange = { end, start };
      const tableCellWithRange = {
        cellRange,
        path: [...context.path, tableRowIndex, tableCellIndex],
        tableCell,
      };

      tableCellsWithRanges.push(tableCellWithRange);
      lastTableCellInColumn.set(startColumnIndex, tableCellWithRange);

      startColumnIndex = endColumnIndex + 1;
    }
  }

  /**
   * Extract paragraphs from each table cell. Do this only after all cells have been processed so
   * that we don't mutate a CellRange object that has already been passed outside the scope of
   * extractParagraphsFromTable.
   */
  return tableCellsWithRanges.flatMap(({ cellRange, path, tableCell }) =>
    extractParagraphsFromContentBlocks(tableCell.children, {
      ...context,
      cellPath: [...context.cellPath, cellRange],
      path,
    })
  );
}
